import {intersection, invert} from 'lodash';

import {isValidPhoneNumber} from '../../../../../libs/shared/helpers/src/utilities/stringHelpers';
import {delayAsync} from '../../../../helpers/asyncHelpers';
import {buildUrlToNavigateToCardInContactsManager} from '../../../../helpers/contactsManagerHelpers';
import {isValidEmailAddress} from '../../../../helpers/emailHelpers';
import {computeIsGlobalNamespace, isTeammateNamespace} from '../../../../helpers/models/namespaceHelpers';
import {ActionWithPayload, batch} from '../../../../helpers/reduxHelpers';
import {isValidTwitterHandle} from '../../../../helpers/twitterHelpers';
import {CardGroupModel} from '../../../../models/api/cardGroupModel';
import {buildContactUid, parseContactUid} from '../../../../models/api/contactModel';
import {AppAction} from '../../../appActions';
import {ThunkAction, ThunkAsyncAction} from '../../../appThunk';
import {
  createCardActionAsync,
  CreateCardConflictError,
  CreateCardTemplate,
  updateCardAvatarAsyncAction,
} from '../../../data/cards/cardsActions';
import {updateDataAction} from '../../../data/dataGroupsActions';
import {navigateToLinkAction} from '../../../stateless/navigationActions';
import {
  showContactsManagerContactCreatedSuccessBannerAction,
  showContactsManagerErrorBannerAction,
} from '../../app/banners/bannersActions';
import {ContactsManagerErrorBannerTypesEnum} from '../../app/banners/bannersState';
import {PanelActionPayload} from '../../panelsActions';
import {getPanels} from '../../panelsSelectors';
import {panelIsContactsManagerPanel} from '../../panelsState';
import {ContactsManagerPanelId} from '../../panelStateBase';
import {makeGetContactsManagerPanel} from '../contactsManager/contactsManagerPanelSelectors';
import {ContactsManagerParametersTypesEnum} from '../contactsManager/parameters/contactsManagerParameters';
import {closeSidePanelAction} from '../contactsManager/sidePanel/contactsManagerSidePanelActions';
import {sidePanelIsCreateContactForm} from '../contactsManager/sidePanel/contactsManagerSidePanelState';
import {contactsManagerTableForceRefreshAction} from '../contactsManager/table/contactsManagerTableActions';
import {
  CreateContactFormFieldTypesEnum,
  CreateContactFormFieldValidityStatusesEnum,
} from './createContactFormState';

/*
 * Type enum.
 */

export enum CreateContactFormActionsEnum {
  // Form state
  UPDATE_NAME = '@@panels/Contacts/create-contact-form/UPDATE_NAME',
  UPDATE_DESCRIPTION = '@@panels/contacts/create-contact-form/UPDATE_DESCRIPTION',
  UPDATE_AVATAR_FILE = '@@panels/contacts/create-contact-form/UPDATE_AVATAR_FILE',
  UPDATE_ACCESS = '@@panels/contacts/create-contact-form/UPDATE_ACCESS',
  UPDATE_ACCOUNT = '@@panels/contacts/create-contact-form/UPDATE_ACCOUNT',
  UPDATE_CUSTOM_ATTRIBUTE = '@@panels/contacts/create-contact-form/UPDATE_CUSTOM_ATTRIBUTE',

  UPDATE_LINK = '@@panels/contacts/create-contact-form/UPDATE_LINK',
  LINK_REMOVED = '@@panels/contacts/create-contact-form/LINK_REMOVED',
  LINK_ADDED = '@@panels/contacts/create-contact-form/LINK_ADDED',

  CARD_GROUP_ADDED = '@@panels/contacts/create-contact-form/CARD_GROUP_ADDED',
  CARD_GROUP_REMOVED = '@@panels/contacts/create-contact-form/CARD_GROUP_REMOVED',

  // Contact Field
  UPDATE_CONTACT_FIELD = '@@panels/contacts/create-contact-form/UPDATE_CONTACT_FIELD',
  CONTACT_FIELD_ADDED = '@@panels/contacts/create-contact-form/CONTACT_FIELD_ADDED',
  CONTACT_FIELD_REMOVED = '@@panels/contacts/create-contact-form/CONTACT_FIELD_REMOVED',
  CONTACT_FIELD_VALIDITY_UPDATED = '@@panels/contacts/create-contact-form/CONTACT_FIELD_VALIDITY_UPDATED',

  // Create contact
  CREATE_CONTACT_STARTED = '@@panels/contacts/create-contact-form/CREATE_CONTACT_STARTED',
  CREATE_CONTACT_COMPLETED = '@@panels/contacts/create-contact-form/CREATE_CONTACT_COMPLETED',
  CREATE_CONTACT_FAILED = '@@panels/contacts/create-contact-form/CREATE_CONTACT_FAILED',
}

/*
 * Types.
 */

export type CreateContactFormCreateContactAsyncHandler = (
  panelId: ContactsManagerPanelId,
  createOptions: CreateCardTemplate,
  avatarFile: File | undefined,
) => Promise<void>;

type ContactsManagerPanelActionPayload = PanelActionPayload<ContactsManagerPanelId>;

interface UpdateNameActionPayload extends ContactsManagerPanelActionPayload {
  name: string;
}

interface UpdateNameAction extends ActionWithPayload<UpdateNameActionPayload> {
  type: CreateContactFormActionsEnum.UPDATE_NAME;
}

interface UpdateDescriptionActionPayload extends ContactsManagerPanelActionPayload {
  description: string;
}

interface UpdateDescriptionAction extends ActionWithPayload<UpdateDescriptionActionPayload> {
  type: CreateContactFormActionsEnum.UPDATE_DESCRIPTION;
}

interface UpdateAvatarFileActionPayload extends ContactsManagerPanelActionPayload {
  avatarFile: File;
}

interface UpdateAvatarFileAction extends ActionWithPayload<UpdateAvatarFileActionPayload> {
  type: CreateContactFormActionsEnum.UPDATE_AVATAR_FILE;
}

interface UpdateAccessActionPayload extends ContactsManagerPanelActionPayload {
  access: string;
}

interface UpdateAccessAction extends ActionWithPayload<UpdateAccessActionPayload> {
  type: CreateContactFormActionsEnum.UPDATE_ACCESS;
}

interface UpdateAccountActionPayload extends ContactsManagerPanelActionPayload {
  accountId: number;
}

interface UpdateAccountAction extends ActionWithPayload<UpdateAccountActionPayload> {
  type: CreateContactFormActionsEnum.UPDATE_ACCOUNT;
}

interface UpdateCustomAttributeActionPayload extends ContactsManagerPanelActionPayload {
  customFieldId: number;
  value: string;
}

interface UpdateCustomAttributeAction extends ActionWithPayload<UpdateCustomAttributeActionPayload> {
  type: CreateContactFormActionsEnum.UPDATE_CUSTOM_ATTRIBUTE;
}

interface CreateContactStartedAction extends ActionWithPayload<ContactsManagerPanelActionPayload> {
  type: CreateContactFormActionsEnum.CREATE_CONTACT_STARTED;
}

interface CreateContactCompletedAction extends ActionWithPayload<ContactsManagerPanelActionPayload> {
  type: CreateContactFormActionsEnum.CREATE_CONTACT_COMPLETED;
}

interface CreateContactFailedAction extends ActionWithPayload<ContactsManagerPanelActionPayload> {
  type: CreateContactFormActionsEnum.CREATE_CONTACT_FAILED;
}

interface UpdateContactFieldActionPayload extends ContactsManagerPanelActionPayload {
  uid: string;
  value: string;
}

interface UpdateContactFieldAction extends ActionWithPayload<UpdateContactFieldActionPayload> {
  type: CreateContactFormActionsEnum.UPDATE_CONTACT_FIELD;
}

interface ContactFieldValidityCheckUpdatedActionPayload extends ContactsManagerPanelActionPayload {
  uid: string;
  validity: CreateContactFormFieldValidityStatusesEnum;
  duplicateCardId?: number;
}

interface ContactFieldValidityCheckUpdatedAction
  extends ActionWithPayload<ContactFieldValidityCheckUpdatedActionPayload> {
  type: CreateContactFormActionsEnum.CONTACT_FIELD_VALIDITY_UPDATED;
}

interface ContactFieldAddedActionPayload extends ContactsManagerPanelActionPayload {
  type: CreateContactFormFieldTypesEnum;
}

interface ContactFieldAddedAction extends ActionWithPayload<ContactFieldAddedActionPayload> {
  type: CreateContactFormActionsEnum.CONTACT_FIELD_ADDED;
}

interface ContactFieldRemovedActionPayload extends ContactsManagerPanelActionPayload {
  uid: string;
}

interface ContactFieldRemovedAction extends ActionWithPayload<ContactFieldRemovedActionPayload> {
  type: CreateContactFormActionsEnum.CONTACT_FIELD_REMOVED;
}

interface UpdateLinkActionPayload extends ContactsManagerPanelActionPayload {
  uid: string;
  value: string;
}

interface UpdateLinkAction extends ActionWithPayload<UpdateLinkActionPayload> {
  type: CreateContactFormActionsEnum.UPDATE_LINK;
}

interface LinkAddedAction extends ActionWithPayload<ContactsManagerPanelActionPayload> {
  type: CreateContactFormActionsEnum.LINK_ADDED;
}

interface LinkRemovedActionPayload extends ContactsManagerPanelActionPayload {
  uid: string;
}

interface LinkRemovedAction extends ActionWithPayload<LinkRemovedActionPayload> {
  type: CreateContactFormActionsEnum.LINK_REMOVED;
}

interface CardGroupAddedOrRemovedActionPayload extends ContactsManagerPanelActionPayload {
  cardGroupId: number;
}

interface CardGroupAddedAction extends ActionWithPayload<CardGroupAddedOrRemovedActionPayload> {
  type: CreateContactFormActionsEnum.CARD_GROUP_ADDED;
}

interface CardGroupRemovedAction extends ActionWithPayload<CardGroupAddedOrRemovedActionPayload> {
  type: CreateContactFormActionsEnum.CARD_GROUP_REMOVED;
}

/*
 * Action creators.
 */

function updateNameAction(panelId: ContactsManagerPanelId, name: string): UpdateNameAction {
  return {
    type: CreateContactFormActionsEnum.UPDATE_NAME,
    payload: {
      panelId,
      name,
    },
  };
}

function updateDescriptionAction(
  panelId: ContactsManagerPanelId,
  description: string,
): UpdateDescriptionAction {
  return {
    type: CreateContactFormActionsEnum.UPDATE_DESCRIPTION,
    payload: {
      panelId,
      description,
    },
  };
}

function updateAvatarFileAction(panelId: ContactsManagerPanelId, avatarFile: File): UpdateAvatarFileAction {
  return {
    type: CreateContactFormActionsEnum.UPDATE_AVATAR_FILE,
    payload: {
      panelId,
      avatarFile,
    },
  };
}

function updateAccessAction(panelId: ContactsManagerPanelId, access: string): UpdateAccessAction {
  return {
    type: CreateContactFormActionsEnum.UPDATE_ACCESS,
    payload: {
      panelId,
      access,
    },
  };
}

function updateAccountAction(panelId: ContactsManagerPanelId, accountId: number): UpdateAccountAction {
  return {
    type: CreateContactFormActionsEnum.UPDATE_ACCOUNT,
    payload: {
      panelId,
      accountId,
    },
  };
}

function updateCustomAttributeAction(
  panelId: ContactsManagerPanelId,
  customFieldId: number,
  value: string,
): UpdateCustomAttributeAction {
  return {
    type: CreateContactFormActionsEnum.UPDATE_CUSTOM_ATTRIBUTE,
    payload: {
      panelId,
      customFieldId,
      value,
    },
  };
}

function createContactStartedAction(panelId: ContactsManagerPanelId): CreateContactStartedAction {
  return {
    type: CreateContactFormActionsEnum.CREATE_CONTACT_STARTED,
    payload: {
      panelId,
    },
  };
}

function createContactCompletedAction(panelId: ContactsManagerPanelId): CreateContactCompletedAction {
  return {
    type: CreateContactFormActionsEnum.CREATE_CONTACT_COMPLETED,
    payload: {
      panelId,
    },
  };
}

function createContactFailedAction(panelId: ContactsManagerPanelId): CreateContactFailedAction {
  return {
    type: CreateContactFormActionsEnum.CREATE_CONTACT_FAILED,
    payload: {
      panelId,
    },
  };
}

function updateContactFieldAction(
  panelId: ContactsManagerPanelId,
  uid: string,
  value: string,
): UpdateContactFieldAction {
  return {
    type: CreateContactFormActionsEnum.UPDATE_CONTACT_FIELD,
    payload: {
      panelId,
      uid,
      value,
    },
  };
}

function contactFieldValidityCheckUpdatedAction(
  panelId: ContactsManagerPanelId,
  uid: string,
  validity: CreateContactFormFieldValidityStatusesEnum,
  duplicateCardId?: number,
): ContactFieldValidityCheckUpdatedAction {
  return {
    type: CreateContactFormActionsEnum.CONTACT_FIELD_VALIDITY_UPDATED,
    payload: {
      panelId,
      uid,
      validity,
      duplicateCardId,
    },
  };
}

function contactFieldAddedAction(
  panelId: ContactsManagerPanelId,
  type: CreateContactFormFieldTypesEnum,
): ContactFieldAddedAction {
  return {
    type: CreateContactFormActionsEnum.CONTACT_FIELD_ADDED,
    payload: {
      panelId,
      type,
    },
  };
}

function contactFieldRemovedAction(panelId: ContactsManagerPanelId, uid: string): ContactFieldRemovedAction {
  return {
    type: CreateContactFormActionsEnum.CONTACT_FIELD_REMOVED,
    payload: {
      panelId,
      uid,
    },
  };
}

function updateLinkAction(panelId: ContactsManagerPanelId, uid: string, value: string): UpdateLinkAction {
  return {
    type: CreateContactFormActionsEnum.UPDATE_LINK,
    payload: {
      panelId,
      uid,
      value,
    },
  };
}

function linkAddedAction(panelId: ContactsManagerPanelId): LinkAddedAction {
  return {
    type: CreateContactFormActionsEnum.LINK_ADDED,
    payload: {
      panelId,
    },
  };
}

function linkRemovedAction(panelId: ContactsManagerPanelId, uid: string): LinkRemovedAction {
  return {
    type: CreateContactFormActionsEnum.LINK_REMOVED,
    payload: {
      panelId,
      uid,
    },
  };
}

function cardGroupAddedAction(panelId: ContactsManagerPanelId, cardGroupId: number): CardGroupAddedAction {
  return {
    type: CreateContactFormActionsEnum.CARD_GROUP_ADDED,
    payload: {
      panelId,
      cardGroupId,
    },
  };
}

function cardGroupRemovedAction(
  panelId: ContactsManagerPanelId,
  cardGroupId: number,
): CardGroupRemovedAction {
  return {
    type: CreateContactFormActionsEnum.CARD_GROUP_REMOVED,
    payload: {
      panelId,
      cardGroupId,
    },
  };
}

/*
 * Union type.
 */

export type CreateContactFormAction =
  | UpdateNameAction
  | UpdateDescriptionAction
  | UpdateAvatarFileAction
  | UpdateAccessAction
  | UpdateAccountAction
  | UpdateCustomAttributeAction
  | UpdateContactFieldAction
  | UpdateLinkAction
  | CreateContactStartedAction
  | CreateContactCompletedAction
  | CreateContactFailedAction
  | ContactFieldValidityCheckUpdatedAction
  | ContactFieldAddedAction
  | ContactFieldRemovedAction
  | LinkAddedAction
  | LinkRemovedAction
  | CardGroupAddedAction
  | CardGroupRemovedAction;

/*
 * Actions.
 */

export function createContactFormUpdateNameAction(
  panelId: ContactsManagerPanelId,
  name: string,
): ThunkAction {
  return (dispatch, getState) => {
    const panel = getPanels(getState())[panelId];
    if (!panel || !panelIsContactsManagerPanel(panel)) {
      return;
    }

    dispatch(updateNameAction(panel.id, name));
  };
}

export function createContactFormUpdateDescriptionAction(
  panelId: ContactsManagerPanelId,
  description: string,
): ThunkAction {
  return (dispatch, getState) => {
    const panel = getPanels(getState())[panelId];
    if (!panel || !panelIsContactsManagerPanel(panel)) {
      return;
    }

    dispatch(updateDescriptionAction(panel.id, description));
  };
}

export function createContactFormUpdateAvatarFileAction(
  panelId: ContactsManagerPanelId,
  avatarFile: File,
): ThunkAction {
  return (dispatch, getState) => {
    const panel = getPanels(getState())[panelId];
    if (!panel || !panelIsContactsManagerPanel(panel)) {
      return;
    }

    dispatch(updateAvatarFileAction(panel.id, avatarFile));
  };
}

export function createContactFormUpdateAccessAction(
  panelId: ContactsManagerPanelId,
  access: string,
): ThunkAction {
  return (dispatch, getState) => {
    const panel = getPanels(getState())[panelId];
    if (!panel || !panelIsContactsManagerPanel(panel)) {
      return;
    }

    dispatch(updateAccessAction(panel.id, access));
  };
}

export function createContactFormUpdateAccountAction(
  panelId: ContactsManagerPanelId,
  accountId: number,
): ThunkAction {
  return (dispatch, getState) => {
    const panel = getPanels(getState())[panelId];
    if (!panel || !panelIsContactsManagerPanel(panel)) {
      return;
    }

    dispatch(updateAccountAction(panel.id, accountId));
  };
}

export function createContactFormUpdateCustomAttributeAction(
  panelId: ContactsManagerPanelId,
  customFieldId: number,
  value: string,
): ThunkAction {
  return (dispatch, getState) => {
    const panel = getPanels(getState())[panelId];
    if (!panel || !panelIsContactsManagerPanel(panel)) {
      return;
    }

    dispatch(updateCustomAttributeAction(panel.id, customFieldId, value));
  };
}

export function createContactFormCreateContactAsyncAction(
  panelId: ContactsManagerPanelId,
  createOptions: CreateCardTemplate,
  avatarFile: File | undefined,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const panel = getPanels(getState())[panelId];
    if (!panel || !panelIsContactsManagerPanel(panel)) {
      return;
    }
    const {parameters, sidePanelState} = panel;

    try {
      dispatch(createContactStartedAction(panelId));

      const newCard = await dispatch(createCardActionAsync(createOptions));

      // Setting the avatar is a separate endpoint so we make a subsequent request
      if (avatarFile) {
        await dispatch(updateCardAvatarAsyncAction(newCard, avatarFile));
      }

      // Naive way but delay a bit to give the backend some time to index the new card.
      await delayAsync(500);

      dispatch(createContactCompletedAction(panelId));
      batch(() => {
        dispatch(showContactsManagerContactCreatedSuccessBannerAction(panelId));
        dispatch(navigateToLinkAction(buildUrlToNavigateToCardInContactsManager(newCard)));
        dispatch(closeSidePanelAction());
        if (
          // On the company contacts page and creating a company contact.
          (computeIsGlobalNamespace(createOptions.namespace) &&
            parameters.type === ContactsManagerParametersTypesEnum.COMPANY_CONTACTS) ||
          // On the private contacts page and creating a private contact.
          (isTeammateNamespace(createOptions.namespace) &&
            parameters.type === ContactsManagerParametersTypesEnum.PRIVATE_CONTACTS)
        ) {
          dispatch(contactsManagerTableForceRefreshAction());
        }
      });
    } catch (error) {
      if (error instanceof CreateCardConflictError && sidePanelIsCreateContactForm(sidePanelState)) {
        // Create contact uids from the contacts.
        const createContactUids = createOptions.contacts.map((contact) =>
          buildContactUid(contact.source, contact.handle),
        );
        // Check for any duplicate contacts.
        const duplicateCardContactUids = intersection(error.conflictingCard.contactUids, createContactUids);

        batch(() => {
          duplicateCardContactUids.forEach((contactUid) => {
            const parsedContactUid = parseContactUid(contactUid);
            const foundFormField = sidePanelState.contactFields.find(
              (field) => field.value.toLowerCase() === parsedContactUid[1].toLowerCase(),
            );
            if (foundFormField) {
              dispatch(
                contactFieldValidityCheckUpdatedAction(
                  panelId,
                  foundFormField.uid,
                  CreateContactFormFieldValidityStatusesEnum.DUPLICATE,
                  error.conflictingCard.id,
                ),
              );
            }
          });
          dispatch(createContactFailedAction(panelId));
        });
        return;
      }

      batch(() => {
        dispatch(
          showContactsManagerErrorBannerAction(
            panelId,
            ContactsManagerErrorBannerTypesEnum.CONTACT_CREATE_FAILED,
          ),
        );
        dispatch(createContactFailedAction(panelId));
      });
    }
  };
}

export function createContactFormUpdateFieldAsyncAction(
  panelId: ContactsManagerPanelId,
  uid: string,
  value: string,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const {sidePanelState} = makeGetContactsManagerPanel()(getState(), {panelId});
    if (!sidePanelIsCreateContactForm(sidePanelState)) {
      return;
    }

    const field = sidePanelState.contactFields.find((f) => f.uid === uid);
    if (!field) {
      return;
    }

    dispatch(updateContactFieldAction(panelId, uid, value));

    if (isCreateContactFormFieldInvalid(field.type, value)) {
      dispatch(
        contactFieldValidityCheckUpdatedAction(
          panelId,
          uid,
          CreateContactFormFieldValidityStatusesEnum.INVALID,
        ),
      );
      return;
    }

    // Check for self duplicates.
    if (
      value &&
      sidePanelState.contactFields
        .filter((f) => f.uid !== uid && f.type === field.type)
        .some((f) => f.value === value)
    ) {
      dispatch(
        contactFieldValidityCheckUpdatedAction(
          panelId,
          uid,
          CreateContactFormFieldValidityStatusesEnum.DUPLICATE_SELF,
        ),
      );
      return;
    }
    dispatch(
      contactFieldValidityCheckUpdatedAction(panelId, uid, CreateContactFormFieldValidityStatusesEnum.VALID),
    );
  };
}

export function createContactFormAddContactFieldAction(
  panelId: ContactsManagerPanelId,
  type: CreateContactFormFieldTypesEnum,
): ThunkAction {
  return (dispatch, getState) => {
    const {sidePanelState} = makeGetContactsManagerPanel()(getState(), {panelId});
    if (!sidePanelIsCreateContactForm(sidePanelState)) {
      return;
    }

    dispatch(contactFieldAddedAction(panelId, type));
  };
}

export function createContactFormRemoveContactFieldAction(
  panelId: ContactsManagerPanelId,
  uid: string,
): ThunkAction {
  return (dispatch, getState) => {
    const {sidePanelState} = makeGetContactsManagerPanel()(getState(), {panelId});
    if (!sidePanelIsCreateContactForm(sidePanelState)) {
      return;
    }

    dispatch(contactFieldRemovedAction(panelId, uid));
  };
}

export function createContactFormUpdateLinkAction(
  panelId: ContactsManagerPanelId,
  uid: string,
  value: string,
): ThunkAction {
  return (dispatch, getState) => {
    const {sidePanelState} = makeGetContactsManagerPanel()(getState(), {panelId});
    if (!sidePanelIsCreateContactForm(sidePanelState)) {
      return;
    }

    dispatch(updateLinkAction(panelId, uid, value));
  };
}

export function createContactFormLinkAddedAction(panelId: ContactsManagerPanelId): ThunkAction {
  return (dispatch, getState) => {
    const {sidePanelState} = makeGetContactsManagerPanel()(getState(), {panelId});
    if (!sidePanelIsCreateContactForm(sidePanelState)) {
      return;
    }

    dispatch(linkAddedAction(panelId));
  };
}

export function createContactFormLinkRemovedAction(
  panelId: ContactsManagerPanelId,
  uid: string,
): ThunkAction {
  return (dispatch, getState) => {
    const {sidePanelState} = makeGetContactsManagerPanel()(getState(), {panelId});
    if (!sidePanelIsCreateContactForm(sidePanelState)) {
      return;
    }

    dispatch(linkRemovedAction(panelId, uid));
  };
}

export function createContactFormCardGroupAddedAction(
  panelId: ContactsManagerPanelId,
  cardGroup: CardGroupModel,
): ThunkAction {
  return (dispatch, getState) => {
    const {sidePanelState} = makeGetContactsManagerPanel()(getState(), {panelId});
    if (!sidePanelIsCreateContactForm(sidePanelState)) {
      return;
    }

    batch(() => {
      dispatch(updateDataAction({cardGroups: [cardGroup]}));
      dispatch(cardGroupAddedAction(panelId, cardGroup.id));
    });
  };
}

export function createContactFormCardGroupRemovedAction(
  panelId: ContactsManagerPanelId,
  cardGroup: CardGroupModel,
): ThunkAction {
  return (dispatch, getState) => {
    const {sidePanelState} = makeGetContactsManagerPanel()(getState(), {panelId});
    if (!sidePanelIsCreateContactForm(sidePanelState)) {
      return;
    }

    dispatch(cardGroupRemovedAction(panelId, cardGroup.id));
  };
}

/*
 * Helpers.
 */

function isCreateContactFormFieldInvalid(type: CreateContactFormFieldTypesEnum, value: string) {
  switch (type) {
    case CreateContactFormFieldTypesEnum.EMAIL:
      return Boolean(value && !isValidEmailAddress(value));
    case CreateContactFormFieldTypesEnum.PHONE_NUMBER:
      return Boolean(value && !isValidPhoneNumber(value));
    case CreateContactFormFieldTypesEnum.TWITTER:
      return Boolean(value && !isValidTwitterHandle(value));
    default:
      return false;
  }
}

/*
 * Type guards.
 */

const createContactFormActionsEnumMap = invert(CreateContactFormActionsEnum);
export function actionIsCreateContactFormAction(action: AppAction): action is CreateContactFormAction {
  return action.type in createContactFormActionsEnumMap;
}
